<script setup lang="ts">
import { ref, reactive } from "vue";
import { flushArray } from "@zyzgroup/core-common";
import { getDocumentElement } from "@zyzgroup/core-web";
import { plateKeyboard } from "../../globalTeleport";
import Box from "../base/Box.vue";
import SVGCheck from "../svg/SVGCheck.vue";

// for merge ce shadow-root styles
defineOptions({
  customOptions: { SVGCheck },
  inheritAttrs: true
});

const props = withDefaults(
  defineProps<{
    maxWidth?: string;
    showBottomInfo?: boolean;
    stylePattern?: "simple" | "dot";
    supportWhitePlate?: boolean;
  }>(),
  {
    maxWidth: "750px",
    showBottomInfo: true,
    stylePattern: "simple",
    supportWhitePlate: false
  }
);

const emits = defineEmits<{
  (event: "clear"): void;
  (event: "complete", type: string, plate: string): void;
}>();

const PLATE_TYPES = [
  {
    name: "蓝牌",
    style: "background-color: #0078f0;",
    completePlateLength: 7,
    symbol: "b"
  },
  {
    name: "绿牌",
    style: "background-color: #00f08c;",
    completePlateLength: 8,
    symbol: "g"
  },
  {
    name: "黄牌",
    style: "background-color: #fac800;",
    completePlateLength: 7,
    symbol: "y"
  },
  {
    name: "黄绿",
    style: "background: linear-gradient(90deg, #fac800 51.5%, #00f08c 51.51%);",
    completePlateLength: 8,
    symbol: "q"
  },
  {
    name: "黑牌",
    style: "background-color: #222222;",
    completePlateLength: 7,
    symbol: "d"
  }
];
if (props.supportWhitePlate) {
  PLATE_TYPES.push({
    name: "白牌",
    style: "background-color: #ffffff;border: 1px solid #222222;",
    completePlateLength: 7,
    symbol: "w"
  });
}

const currentTypeIndex = ref(0);

const plates = reactive<string[]>([]);

const focusIndex = ref(-1);

const isPlateKeyboardOpen = ref(false);
const scrollTopWhenKeyboardShow = ref(0);

const numbersRef = ref();
let numbersTop = 0;

function openPlateKeyboard() {
  plateKeyboard({
    initPlates: plates,
    completePlateLength:
      PLATE_TYPES[currentTypeIndex.value].completePlateLength,
    onPlates: (newPlates: string[]) => {
      flushArray(plates, newPlates);
      focusIndex.value = plates.length;
      if (
        plates.length ===
        PLATE_TYPES[currentTypeIndex.value].completePlateLength
      ) {
        emits(
          "complete",
          PLATE_TYPES[currentTypeIndex.value].name,
          plates.join("")
        );
      }
    },
    onOpen: () => {
      // 页面其余元素变化 导致 el.rect.x 或 el.rect.y 变化时，也要触发
      // const systemHeight = getDocumentElement().clientHeight;
      if (isPlateKeyboardOpen.value == false) {
        // 初次打开时才变化
        const rect = numbersRef.value.getBoundingClientRect();
        numbersTop = rect.y + rect.height;
      }
      isPlateKeyboardOpen.value = true;
    },
    onClose: () => {
      isPlateKeyboardOpen.value = false;
    },
    onResize: (rect) => {
      scrollTopWhenKeyboardShow.value =
        numbersTop <= rect.y ? 0 : rect.y - numbersTop;
      // console.log(numbersTop, rect.y, scrollTopWhenKeyboardShow.value);
    }
  });
}

const changePlateType = (index: number) => {
  currentTypeIndex.value = index;
  if (
    focusIndex.value <= PLATE_TYPES[index].completePlateLength &&
    plates.length < PLATE_TYPES[index].completePlateLength
  ) {
    focusIndex.value = plates.length;
    openPlateKeyboard();
    emits("clear");
  } else {
    flushArray(plates, plates.slice(0, PLATE_TYPES[index].completePlateLength));
  }
  if (PLATE_TYPES[index].completePlateLength === plates.length) {
    emits(
      "complete",
      PLATE_TYPES[currentTypeIndex.value].name,
      plates.join("")
    );
  }
};

const tapNumber = (index: number) => {
  flushArray(plates, plates.slice(0, index));
  focusIndex.value = Math.min(Math.max(0, focusIndex.value), index);
  openPlateKeyboard();
  emits("clear");
};

defineExpose({});
</script>

<template>
  <div
    class="plate-mobile"
    :style="{
      maxWidth: props.maxWidth,
      transform: isPlateKeyboardOpen
        ? `translate(0, ${scrollTopWhenKeyboardShow}px)`
        : ''
    }"
  >
    <div class="title">车牌颜色</div>
    <div
      class="plate-types"
      :class="props.stylePattern"
    >
      <div
        v-for="(item, index) in PLATE_TYPES"
        :key="index"
        :class="index == currentTypeIndex ? 'checked' : ''"
        @click="changePlateType(index)"
      >
        <template v-if="props.stylePattern == 'dot'">
          <div :style="item.style"></div>
          <div>{{ item.name }}</div>
          <div>
            <SVGCheck
              color="#ffffff"
              width="7px"
            ></SVGCheck>
          </div>
        </template>
        <template v-else>
          <div>{{ item.name }}</div>
        </template>
      </div>
    </div>
    <div class="title">车牌号码</div>
    <div ref="numbersRef">
      <div class="plate-numbers">
        <div
          @click="tapNumber(0)"
          :class="0 == focusIndex ? 'current' : ''"
        >
          {{ plates[0] }}
        </div>
        <div
          @click="tapNumber(1)"
          :class="1 == focusIndex ? 'current' : ''"
        >
          {{ plates[1] }}
        </div>
        <!-- <div class="dot">·</div> -->
        <div
          v-for="index in PLATE_TYPES[currentTypeIndex].completePlateLength - 2"
          :key="index"
          :class="index + 1 == focusIndex ? 'current' : ''"
          @click="tapNumber(index + 1)"
        >
          {{ plates[index + 1] }}
        </div>
      </div>
    </div>
    <div
      class="info"
      v-if="props.showBottomInfo"
    >
      请输入您真实有效的车牌号，便于正常使用系统功能。
    </div>
  </div>
</template>

<style scoped>
.plate-mobile {
  background-color: #ffffff;
  overflow: hidden;
  border-radius: 8px;
  padding-bottom: 15px;
  padding-left: 15px;
  padding-right: 15px;
}
.title {
  font-size: 16px;
  line-height: 1;
  color: #333333;
  font-weight: bold;
  text-align: center;
  margin-top: 20px;
  margin-bottom: 10px;
}
.plate-types {
  display: flex;
  justify-content: center;
}
.plate-types > div {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 60px;
  height: 44px;
  border-radius: 4px;
  background-color: #f5f5f5;
  cursor: pointer;
  overflow: hidden;
  box-sizing: border-box;
}
.plate-types > div + div {
  margin-left: 5px;
}
.plate-types.dot > div > div:nth-child(1) {
  width: 14px;
  height: 14px;
  margin-bottom: 5px;
  border-radius: 50%;
  background-color: #0078f0;
}
.plate-types.dot > div:nth-child(2) > div:nth-child(1) {
  background-color: #00f08c;
}
.plate-types.dot > div:nth-child(3) > div:nth-child(1) {
  background-color: #fac800;
}
.plate-types.dot > div:nth-child(4) > div:nth-child(1) {
  background: linear-gradient(90deg, #fac800 51.5%, #00f08c 51.51%);
}
.plate-types.dot > div:nth-child(5) > div:nth-child(1) {
  background-color: #222222;
}
.plate-types.dot > div > div:nth-child(2) {
  color: #cccccc;
  font-size: 14px;
  line-height: 1;
  font-weight: bold;
}
.plate-types.dot > div > div:nth-child(3) {
  display: none;
  position: absolute;
  right: -1px;
  bottom: -1px;
  width: 16px;
  height: 16px;
  border-radius: 16px 0 4px 0;
  background: #5a82f0;
}
.plate-types.dot > div > div:nth-child(3) > svg {
  position: absolute;
  top: 6px;
  left: 6px;
}
.plate-types.dot > div.checked {
  border: 1px solid #5a82f0;
}
.plate-types.dot > div.checked > div:nth-child(2) {
  color: #333333;
}
.plate-types.dot > div.checked > div:nth-child(3) {
  display: block;
}
.plate-types.simple > div > div:nth-child(1) {
  color: #666666;
  font-size: 14px;
  line-height: 1;
  font-weight: bold;
}
.plate-types.simple > div.checked > div:nth-child(1) {
  color: #000000;
}
.plate-types.simple > div.checked:nth-child(1) {
  background-color: #0078f0;
}
.plate-types.simple > div.checked:nth-child(1) > div {
  color: #ffffff;
}
.plate-types.simple > div.checked:nth-child(2) {
  background-color: #00f08c;
}
.plate-types.simple > div.checked:nth-child(3) {
  background-color: #fac800;
}
.plate-types.simple > div.checked:nth-child(4) {
  background: linear-gradient(90deg, #fac800 51.5%, #00f08c 51.51%);
}
.plate-types.simple > div.checked:nth-child(5) {
  background-color: #222222;
}
.plate-types.simple > div.checked:nth-child(5) > div {
  color: #ffffff;
}
.plate-types.simple > div.checked:nth-child(6) {
  border: 1px solid #222222;
  background-color: #ffffff;
}
.plate-numbers {
  display: flex;
  justify-content: center;
  align-items: center;
}
.plate-numbers > div {
  flex: 1;
  max-width: 40px;
  height: 44px;
  line-height: 44px;
  border-radius: 4px;
  font-size: 18px;
  color: #333333;
  text-align: center;
  border: 1px solid #cccccc;
}
.plate-numbers > div.current {
  border: 1px solid #5a82f0;
}
.plate-numbers > div + div {
  margin-left: 5px;
}
.plate-numbers > div.dot {
  flex: 0;
  border: 0;
  padding: 0 2.5px;
  color: #cccccc;
  font-size: 46px;
  margin-right: -5px;
  margin-left: 0;
}
.info {
  font-size: 12px;
  line-height: 16px;
  font-weight: 400;
  color: #999999;
  text-align: center;
  padding: 0 5px;
  margin-top: 20px;
}
</style>
